import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@safetyhub/ui';
import { getOperationsHealth, type AlertLevel, type CronTask } from '../lib/operations';

// alertLevel 배지 스타일
const ALERT_LEVEL_CLASSES: Record<AlertLevel, string> = {
  OK: 'bg-green-100 text-green-800',
  WARN: 'bg-yellow-100 text-yellow-800',
  CRITICAL: 'bg-red-100 text-red-800',
};

const ALERT_LEVEL_LABELS: Record<AlertLevel, string> = {
  OK: '정상',
  WARN: '경고',
  CRITICAL: '위급',
};

function AlertBadge({ level }: { level: AlertLevel }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ALERT_LEVEL_CLASSES[level]}`}>
      {ALERT_LEVEL_LABELS[level]}
    </span>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm font-medium ${highlight ? 'text-red-600' : 'text-gray-900'}`}>{value}</span>
    </div>
  );
}

interface HealthCardProps {
  title: string;
  alertLevel: AlertLevel;
  linkTo?: string | null;
  children: React.ReactNode;
}

function HealthCard({ title, alertLevel, linkTo, children }: HealthCardProps) {
  const navigate = useNavigate();

  return (
    // eslint-disable-next-line local/no-self-painted-card -- 경보 등급을 유채색 왼쪽 굵은 테두리로 표시하는 Callout 갈래.
    // Card는 사방 1px 중성 테두리를 소유하므로 여기에 border-l-4 + 색을 덧대면 테두리 전체가 물든다.
    <div className={`bg-white rounded-card-lg shadow p-4 flex flex-col gap-2 border-l-4 ${
      alertLevel === 'CRITICAL' ? 'border-red-500' : alertLevel === 'WARN' ? 'border-yellow-400' : 'border-green-400'
    }`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <AlertBadge level={alertLevel} />
      </div>
      <div className="flex-1">{children}</div>
      {linkTo && (
        <button
          onClick={() => navigate(linkTo)}
          className="mt-2 text-xs text-brand-600 hover:text-brand-800 hover:underline text-right"
        >
          자세히 보기 →
        </button>
      )}
    </div>
  );
}

function formatRelative(dateStr: string | null): string {
  if (!dateStr) return '없음';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '방금';
  if (mins < 60) return `${mins}분 전`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}시간 전`;
  return `${Math.floor(hrs / 24)}일 전`;
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const CRON_TASK_LABELS: Record<string, string> = {
  EducationReminderTask: '교육 리마인더',
  InspectionScheduleReminderTask: '점검 일정 리마인더',
  PdfCleanupTask: 'PDF 정리',
  PurgeTask: '데이터 삭제',
  ScheduleChangeReminderTask: '일정 변경 리마인더',
  SmsDailyStatsTask: 'SMS 통계',
  TbmExpiryTask: 'TBM 만료',
};

export default function Operations() {
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['operations', 'health'],
    queryFn: getOperationsHealth,
    refetchInterval: 30000,
  });

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('ko-KR')
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        데이터를 불러오는 중...
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-red-600">
          {error instanceof Error ? error.message : '데이터를 불러올 수 없습니다.'}
        </p>
        <Button variant="primary" size="sm" onClick={() => refetch()}>
          다시 시도
        </Button>
      </div>
    );
  }

  const { system, database, redis, pdf, externalApis, cronTasks, queues, verification, aiUsage, collectedAt } = data;

  // MB → GB 변환 헬퍼
  const memUsedGb = (system.memUsedMb ?? 0) / 1024;
  const memTotalGb = (system.memTotalMb ?? 0) / 1024;
  const swapUsedGb = (system.swapUsedMb ?? 0) / 1024;
  const swapTotalGb = (system.swapTotalMb ?? 0) / 1024;
  const memUsagePct = system.memUsedPct ?? 0;

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">운영 모니터링</h2>
          <p className="text-sm text-gray-500 mt-1">
            스냅샷 기준: {formatTime(collectedAt)} · 마지막 갱신: {lastUpdated ?? '-'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-control text-sm hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          새로고침
        </button>
      </div>

      {/* 카드 그리드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

        {/* 1. 시스템 리소스 */}
        <HealthCard title="시스템 리소스" alertLevel={system.alertLevel}>
          <Stat label="CPU" value={`${system.cpuUsage ?? 0}% (load ${(system.loadAvg1m ?? 0).toFixed(2)})`} />
          <Stat label="메모리" value={`${memUsedGb.toFixed(1)} / ${memTotalGb.toFixed(1)} GB (${memUsagePct}%)`} />
          <div className="w-full bg-gray-200 rounded-full h-1.5 my-1">
            <div
              className={`h-1.5 rounded-full ${memUsagePct >= 90 ? 'bg-red-500' : memUsagePct >= 70 ? 'bg-yellow-400' : 'bg-green-500'}`}
              style={{ width: `${memUsagePct}%` }}
            />
          </div>
          <Stat
            label="Swap"
            value={`${swapUsedGb.toFixed(1)} / ${swapTotalGb.toFixed(1)} GB (${system.swapUsedPct ?? 0}%)`}
            highlight={(system.swapUsedPct ?? 0) > 50}
          />
          <Stat
            label="디스크"
            value={`${system.diskUsedGb ?? 0} / ${system.diskTotalGb ?? 0} GB (${system.diskUsedPct ?? 0}%)`}
            highlight={(system.diskUsedPct ?? 0) >= 90}
          />
          <Stat label="가동 시간" value={`${system.uptimeDays ?? 0}일`} />
        </HealthCard>

        {/* 2. 데이터베이스 */}
        <HealthCard title="데이터베이스" alertLevel={database.alertLevel}>
          <Stat
            label="ping"
            value={database.pingMs != null ? `${database.pingMs} ms` : '-'}
            highlight={(database.pingMs ?? 0) > 100}
          />
          <Stat label="활성 연결" value={database.activeConnections ?? '-'} />
          <Stat label="슬로우 쿼리" value={database.slowQueries ?? '-'} highlight={(database.slowQueries ?? 0) > 0} />
        </HealthCard>

        {/* 3. Redis */}
        <HealthCard title="Redis" alertLevel={redis.alertLevel}>
          <Stat
            label="ping"
            value={redis.pingMs != null ? `${redis.pingMs} ms` : '-'}
            highlight={(redis.pingMs ?? 0) > 50}
          />
          <Stat
            label="메모리"
            value={redis.memoryHuman ?? (redis.memoryMb != null ? `${redis.memoryMb} MB` : '-')}
          />
          <Stat label="키 수" value={(redis.keys ?? 0).toLocaleString('ko-KR')} />
        </HealthCard>

        {/* 4. PDF 처리 */}
        <HealthCard title="PDF 처리" alertLevel={pdf.alertLevel}>
          <Stat
            label="Chrome 프로세스"
            value={pdf.chromeProcessCount ?? '-'}
            highlight={(pdf.chromeProcessCount ?? 0) > 5}
          />
          <Stat label="처리 중" value={pdf.activeBrowserRunning ? 'YES' : 'NO'} />
          <Stat label="누적 처리" value={`${(pdf.totalProcessed ?? 0).toLocaleString('ko-KR')}건`} />
          <Stat
            label="누적 실패"
            value={`${(pdf.totalFailed ?? 0).toLocaleString('ko-KR')}건`}
            highlight={(pdf.totalFailed ?? 0) > 0}
          />
          <Stat
            label="마지막 정리"
            value={pdf.lastCleanup?.at
              ? `${formatRelative(pdf.lastCleanup.at)} (${pdf.lastCleanup.killedCount}건)`
              : '없음'}
            highlight={pdf.lastCleanup?.hasError ?? false}
          />
        </HealthCard>

        {/* 5. 외부 API */}
        <HealthCard title="외부 API" alertLevel={externalApis.alertLevel} linkTo="/external-services">
          <div className="space-y-1.5">
            <div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-gray-600">국세청 (NTS)</span>
                {externalApis.nts.lastCallAt ? (
                  <span className={`text-xs ${externalApis.nts.success ? 'text-green-600' : 'text-red-600'}`}>
                    {externalApis.nts.success ? '성공' : '실패'}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">데이터 없음</span>
                )}
              </div>
              {externalApis.nts.lastCallAt && (
                <div className="text-xs text-gray-400">
                  {formatRelative(externalApis.nts.lastCallAt)} · {externalApis.nts.latencyMs ?? '-'}ms
                  {externalApis.nts.lastValidMsg && <span className="ml-1 text-gray-500">({externalApis.nts.lastValidMsg})</span>}
                </div>
              )}
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-gray-600">공공데이터</span>
                {externalApis.publicData.lastCallAt === null ? (
                  <span className="text-xs text-gray-400">미구현</span>
                ) : (
                  <span className={`text-xs ${externalApis.publicData.success ? 'text-green-600' : 'text-red-600'}`}>
                    {externalApis.publicData.success ? '성공' : '실패'}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-gray-600">SMS</span>
                {externalApis.sms?.lastCallAt == null ? (
                  <span className="text-xs text-gray-400">데이터 없음</span>
                ) : (
                  <span className={`text-xs ${externalApis.sms.success ? 'text-green-600' : 'text-red-600'}`}>
                    {externalApis.sms.success ? '성공' : '실패'} · {externalApis.sms.latencyMs ?? '-'}ms
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">AI Providers</div>
              {(['gemini', 'claude', 'openai'] as const).map((provider) => {
                const entry = externalApis.aiProviders?.[provider];
                return (
                  <div key={provider} className="flex justify-between items-center pl-2">
                    <span className="text-xs text-gray-500 capitalize">{provider}</span>
                    {entry?.lastCallAt == null ? (
                      <span className="text-xs text-gray-400">데이터 없음</span>
                    ) : (
                      <span className={`text-xs ${entry.success ? 'text-green-600' : 'text-red-600'}`}>
                        {entry.success ? '성공' : '실패'} · {entry.latencyMs ?? '-'}ms
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </HealthCard>

        {/* 6. Cron 작업 */}
        <HealthCard title="Cron 작업" alertLevel="OK" linkTo="/tasks">
          <div className="max-h-48 overflow-y-auto space-y-1">
            {cronTasks.map((task: CronTask) => (
              <div key={task.name} className="flex items-center justify-between">
                <span className="text-xs text-gray-600 truncate mr-2">
                  {CRON_TASK_LABELS[task.name] ?? task.name}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {task.lastRunAt ? (
                    <>
                      <span className={`inline-block w-2 h-2 rounded-full ${task.success === false ? 'bg-red-500' : 'bg-green-500'}`} />
                      <span className="text-xs text-gray-400">{formatRelative(task.lastRunAt)}</span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">미실행</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </HealthCard>

        {/* 7. 큐/Job */}
        <HealthCard title="큐 / Job" alertLevel={queues.alertLevel}>
          <div className="space-y-2">
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">사진 분석 (photoAnalysis)</div>
              <div className="flex gap-3 pl-2">
                <Stat
                  label="진행 중"
                  value={queues.photoAnalysis?.active ?? '-'}
                  highlight={(queues.photoAnalysis?.active ?? 0) > 0}
                />
                <Stat label="대기" value={queues.photoAnalysis?.waiting ?? '-'} />
                <Stat
                  label="실패"
                  value={queues.photoAnalysis?.failed ?? '-'}
                  highlight={(queues.photoAnalysis?.failed ?? 0) > 0}
                />
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">임포트 (import)</div>
              <div className="pl-2">
                <Stat label="진행 중" value={queues.import?.active ?? '-'} />
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">매칭 (match)</div>
              <div className="pl-2">
                <Stat label="진행 중" value={queues.match?.active ?? '-'} />
              </div>
            </div>
          </div>
        </HealthCard>

        {/* 8. 조직 인증 */}
        <HealthCard title="조직 인증" alertLevel={verification.alertLevel} linkTo="/verification">
          <Stat label="PENDING" value={verification.pending ?? 0} />
          <Stat
            label="에스컬레이션"
            value={verification.escalated ?? 0}
            highlight={(verification.escalated ?? 0) > 0}
          />
          <Stat
            label="폐업 발견"
            value={verification.businessClosed ?? 0}
            highlight={(verification.businessClosed ?? 0) > 0}
          />
        </HealthCard>

        {/* 9. AI 사용량 */}
        <HealthCard title="AI 사용량" alertLevel="OK" linkTo="/ai-usage">
          <Stat label="오늘 호출" value={`${(aiUsage.callsToday ?? 0).toLocaleString('ko-KR')}회`} />
          <Stat label="오늘 비용" value={`$${(aiUsage.costToday ?? 0).toFixed(3)}`} />
          <Stat label="이번 달 호출" value={`${(aiUsage.callsThisMonth ?? 0).toLocaleString('ko-KR')}회`} />
          <Stat label="이번 달 비용" value={`$${(aiUsage.costThisMonth ?? 0).toFixed(2)}`} />
        </HealthCard>

      </div>
    </div>
  );
}
